import { ContactQueue } from "./portal/ContactQueue";
import { ClientExtensions } from "./portal/ClientExtensions";
import { ClientCsvPicker } from "./ClientCsvPicker";
import { switchRows } from "./client-csv";
import type { PortalTab } from "./portal/PortalShell";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  BadgeDollarSign,
  BellRing,
  ClipboardCheck,
  Clock3,
  Download,
  FileSpreadsheet,
  Link2,
  Plus,
  PackageOpen,
  Search,
  Trash2,
  Upload,
  Users,
} from "lucide-react";
import { API_URL, api, apiForm, errorMessage, track } from "./api";
import { ClientImportRow } from "./client-csv";
import {
  BookingHistoryImportRow,
  parseBookingHistoryCsv,
  parseServiceCsv,
  ServiceImportRow,
} from "./operation-csv";
import {
  fetchNotificationSettings,
  isIos,
  pushCapability,
  registerServiceWorker,
  saveNotificationPreferences,
  sendTestPush,
  subscribeToPush,
  unsubscribeFromPush,
  type NotificationPreferences,
  type NotificationSettings,
  type PushCapability,
} from "./push";

// 예약·리마인더 상태는 DB의 영문 코드다. 한국어 화면에 그대로 노출하면
// 디자이너가 "pending"이 확인 대기인지 결제 대기인지 알 수 없다.
// App.tsx의 StatusPill과 같은 어휘를 쓴다.
// HB-NOTIFY-01 방해 금지·아침 요약 시각 선택지. 0~23시를 한국어 표기로 고정한다.
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => (
  <option key={hour} value={hour}>
    {String(hour).padStart(2, "0")}시
  </option>
));

const BOOKING_STATUS_LABELS: Record<string, string> = {
  pending: "확인 대기",
  confirmed: "예약 확정",
  completed: "완료",
  cancelled: "취소",
  no_show: "노쇼",
};

const REMINDER_STATUS_LABELS: Record<string, string> = {
  pending: "발송 대기",
  scheduled: "발송 대기",
  sent: "발송 완료",
  skipped: "건너뜀",
  failed: "발송 실패",
  cancelled: "취소됨",
};

function bookingStatusLabel(status: string): string {
  return BOOKING_STATUS_LABELS[status] || status;
}

function reminderStatusLabel(status: string): string {
  return REMINDER_STATUS_LABELS[status] || status;
}

interface ServiceOption {
  id: string;
  name: string;
  duration_minutes: number;
  is_active: number;
}

interface Client {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  birthday?: string | null;
  tags?:string;
  preferred_style?: string | null;
  service_formula?: string | null;
  private_notes?: string | null;
  booking_count?: number;
  last_visit_at?: string | null;
  media_count?: number;
}

interface ClientDetail {
  client: Client;
  history: Array<{
    id: string;
    scheduled_at: string;
    status: string;
    customer_notes: string | null;
    service_name: string;
    price: number;
  }>;
  media: Array<{
    id: string;
    caption: string | null;
    created_at: string;
    asset_path: string;
  }>;
}

interface CalendarBooking {
  series_id?:string; deposit_status?:string;
  id: string;
  client_id: string | null;
  customer_name: string;
  customer_phone: string;
  customer_email: string | null;
  scheduled_at: string;
  duration_minutes: number;
  status: string;
  source: string;
  acquisition_source: string;
  intake_answers: string;
  service_name: string;
  service_id: string;
  consultation_status: "pending" | "completed";
  consultation_desired_result: string | null;
  consultation_recent_chemical_history: string | null;
  consultation_scalp_sensitivity: "unknown" | "none" | "sometimes" | "frequent" | null;
  consultation_reaction_history: string | null;
}

interface WaitlistState {
  deliveryEnabled: boolean;
  entries: Array<{
    id: string;
    customer_name: string;
    customer_phone: string;
    customer_email: string | null;
    desired_date: string;
    time_window: "any" | "morning" | "afternoon" | "evening";
    note: string | null;
    status: "waiting" | "notified" | "booked" | "withdrawn" | "expired";
    service_name: string;
    acquisition_source: string;
  }>;
}

interface CalendarBlock {
  id: string;
  starts_at: string;
  ends_at: string;
  kind: string;
  note: string | null;
}

interface ImportPreview {
  rows: Array<{
    rowId: string;
    action: "create" | "update" | "skip" | "error";
    error: string | null;
  }>;
  summary: { create: number; update: number; skip: number; error: number };
}

interface ReminderState {
  deliveryEnabled: boolean;
  reminders: Array<{
    id: string;
    kind: "booking_reminder_24h" | "booking_reminder_2h";
    scheduled_for: string;
    status: string;
    customer_name: string;
    customer_phone: string;
    customer_email: string | null;
    scheduled_at: string;
    service_name: string;
  }>;
}

interface CraftCareProfile {
  sensitivities: string | null;
  reaction_history: string | null;
  patch_test_status: "not_needed" | "needed" | "scheduled" | "passed" | "reaction";
  patch_test_at: string | null;
  consent_scope: string | null;
  consent_recorded_at: string | null;
  updated_at: string;
}

interface TreatmentRecord {
  id: string;
  occurred_at: string;
  service_id: string | null;
  service_name: string;
  formula: string | null;
  process_notes: string | null;
  processing_minutes: number;
  actual_price: number;
  material_cost: number;
  outcome: "as_planned" | "adjusted" | "follow_up_needed";
  aftercare: string | null;
  rebooking_weeks: number;
}

interface InventoryItem {
  id: string;
  name: string;
  category: string;
  unit: string;
  quantity: number;
  reorder_point: number;
  unit_cost: number;
  archived: number;
}

interface CraftOverview {
  days: number;
  metrics: {
    treatments: number;
    revenue: number;
    materialCost: number;
    contribution: number;
    bookedMinutes: number;
    revenuePerHour: number;
    contributionRate: number;
  };
  rebooking: Array<{
    client_id: string;
    client_name: string;
    phone: string;
    service_name: string;
    dueAt: string;
    overdueDays: number;
    draft: string;
  }>;
  inventory: InventoryItem[];
  lowStock: number;
}

type Tab = "calendar" | "clients" | "operations" | "waitlist" | "transfer" | "connect";
type OperationImport =
  | { kind: "services"; rows: ServiceImportRow[] }
  | { kind: "booking_history"; rows: BookingHistoryImportRow[] };

const koDate = new Intl.DateTimeFormat("ko-KR", {
  timeZone: "Asia/Seoul",
  month: "short",
  day: "numeric",
  weekday: "short",
});
const koTime = new Intl.DateTimeFormat("ko-KR", {
  timeZone: "Asia/Seoul",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

function dateKey(value: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(value));
  const valueOf = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value || "";
  return `${valueOf("year")}-${valueOf("month")}-${valueOf("day")}`;
}

function todayKey(): string {
  return dateKey(new Date().toISOString());
}

function shiftDate(value: string, days: number): string {
  const date = new Date(`${value}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function startOfWeek(value: string): string {
  const day = new Date(`${value}T12:00:00Z`).getUTCDay();
  return shiftDate(value, -(day === 0 ? 6 : day - 1));
}

function seoulIso(value: FormDataEntryValue | null): string {
  return new Date(`${String(value)}:00+09:00`).toISOString();
}

function seoulInputNow(): string {
  return new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 16);
}

function won(value: number): string {
  return `${Math.round(Number(value) || 0).toLocaleString("ko-KR")}원`;
}

function Field({
  label,
  name,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  name: string;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <input name={name} {...props} />
    </label>
  );
}

function AuthenticatedImage({
  path,
  token,
  alt,
}: {
  path: string;
  token: string;
  alt: string;
}) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let objectUrl = "";
    void fetch(`${API_URL}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        if (!response.ok) throw new Error("media_load_failed");
        return response.blob();
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path, token]);
  return src ? (
    <img src={src} alt={alt} />
  ) : (
    <span className="media-placeholder">불러오는 중</span>
  );
}

export function BusinessWorkspace({
  token,
  services,
  onChanged,
  portalTab,
}: {
  token: string;
  portalTab?: PortalTab;
  services: ServiceOption[];
  onChanged: () => Promise<void>;
}) {
  const [tab, setTab] = useState<Tab>("calendar");
  useEffect(() => { if (portalTab) setTab(portalTab === "today" ? "operations" : portalTab === "mypage" ? "transfer" : portalTab); }, [portalTab]);
  // HB-NOTIFY-01 — docs/product/notification-channel.md
  const [notify, setNotify] = useState<NotificationSettings | null>(null);
  const [notifyCapability, setNotifyCapability] = useState<PushCapability>({
    state: "unsupported",
    reason: "no_service_worker",
  });
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<ClientDetail | null>(
    null,
  );
  const [craftClient, setCraftClient] = useState<{
    care: CraftCareProfile | null;
    treatments: TreatmentRecord[];
  } | null>(null);
  const [craftOverview, setCraftOverview] = useState<CraftOverview | null>(null);
  const [craftRange, setCraftRange] = useState<30 | 90>(30);
  const [clientQuery, setClientQuery] = useState("");
  const [calendarDate, setCalendarDate] = useState(todayKey());
  const [calendarView, setCalendarView] = useState<"day" | "week" | "month">("week");
  const [bookings, setBookings] = useState<CalendarBooking[]>([]);
  const [blocks, setBlocks] = useState<CalendarBlock[]>([]);
  const [importRows, setImportRows] = useState<ClientImportRow[]>([]);
  const [importMode, setImportMode] = useState<"skip" | "merge">("skip");
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [operationImport, setOperationImport] = useState<OperationImport | null>(null);
  const [operationPreview, setOperationPreview] = useState<ImportPreview | null>(null);
  const [operationAttested, setOperationAttested] = useState(false);
  const [waitlist, setWaitlist] = useState<WaitlistState>({
    deliveryEnabled: false,
    entries: [],
  });
  const [feedUrl, setFeedUrl] = useState("");
  const [reminderState, setReminderState] = useState<ReminderState>({
    deliveryEnabled: false,
    reminders: [],
  });
  const [message, setMessage] = useState("");
  const [dateOverrides,setDateOverrides]=useState<Array<{date:string;closed:number}>>([]);
  const [mediaSelection,setMediaSelection]=useState<string[]>([]);
  const [tagFilter,setTagFilter]=useState("");
  const [seriesSummary,setSeriesSummary]=useState("");
  const [busy, setBusy] = useState(false);

  const range = useMemo(() => {
    const start =
      calendarView === "month" ? `${calendarDate.slice(0,7)}-01` : calendarView === "week" ? startOfWeek(calendarDate) : calendarDate;
    const end = shiftDate(start, calendarView === "month" ? new Date(Number(start.slice(0,4)),Number(start.slice(5,7)),0).getDate() : calendarView === "week" ? 7 : 1);
    return {
      start,
      end,
      from: new Date(`${start}T00:00:00+09:00`).toISOString(),
      to: new Date(`${end}T00:00:00+09:00`).toISOString(),
    };
  }, [calendarDate, calendarView]);

  useEffect(()=>{if(portalTab === "clients"){const id=new URLSearchParams(window.location.search).get("client");if(id)void openClient(id).catch(error=>setMessage(errorMessage(error)));}},[portalTab]);
  const loadClients = useCallback(
    async (query = "") => {
      const data = await api<Client[]>(
        `/api/v1/business/clients?q=${encodeURIComponent(query)}`,
        {},
        token,
      );
      setClients(data);
    },
    [token],
  );

  const loadCalendar = useCallback(async () => {
    const data = await api<{
      bookings: CalendarBooking[];
      blocks: CalendarBlock[];dateOverrides?:Array<{date:string;closed:number}>;
    }>(
      `/api/v1/business/calendar?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`,
      {},
      token,
    );
    setBookings(data.bookings);
    setBlocks(data.blocks);
    setDateOverrides(data.dateOverrides||[]);
  }, [range.from, range.to, token]);

  const loadReminders = useCallback(async () => {
    setReminderState(
      await api<ReminderState>("/api/v1/business/reminders", {}, token),
    );
  }, [token]);

  const loadNotificationSettings = useCallback(async () => {
    setNotify(await fetchNotificationSettings(token));
    setNotifyCapability(pushCapability());
  }, [token]);

  const loadWaitlist = useCallback(async () => {
    setWaitlist(await api<WaitlistState>("/api/v1/business/waitlist", {}, token));
  }, [token]);

  const loadCraftOverview = useCallback(async (days: 30 | 90 = craftRange) => {
    setCraftOverview(
      await api<CraftOverview>(`/api/v1/craft/overview?days=${days}`, {}, token),
    );
  }, [craftRange, token]);

  useEffect(() => {
    void Promise.all([loadClients(), loadCalendar(), loadReminders(), loadWaitlist()]).catch(
      (error) => setMessage(errorMessage(error)),
    );
  }, [loadCalendar, loadClients, loadReminders, loadWaitlist]);

  useEffect(() => {
    if (tab !== "operations") return;
    void loadCraftOverview(craftRange).catch((error) => setMessage(errorMessage(error)));
  }, [craftRange, loadCraftOverview, tab]);

  // HB-NOTIFY-01 — 알림 탭에서만 설정을 읽는다. service worker는 미리 등록해
  // 구독 버튼을 눌렀을 때 등록 지연이 권한 프롬프트와 겹치지 않게 한다.
  const enablePush = useCallback(async () => {
    if (!notify?.vapidPublicKey) return;
    setBusy(true);
    try {
      track("push_permission_prompted", { platform: isIos() ? "ios" : "other" });
      const result = await subscribeToPush(token, notify.vapidPublicKey);
      if (result.ok) {
        track("push_subscribed", { platform: isIos() ? "ios" : "other" });
        setMessage("이 기기로 알림을 보냅니다.");
        await loadNotificationSettings();
      } else {
        setMessage(
          result.reason === "permission_denied"
            ? "브라우저에서 알림을 차단했어요. 사이트 설정에서 허용으로 바꿔 주세요."
            : "이 기기에서는 알림을 켤 수 없어요.",
        );
        setNotifyCapability(pushCapability());
      }
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }, [loadNotificationSettings, notify?.vapidPublicKey, token]);

  const disablePush = useCallback(
    async (subscriptionId: string) => {
      setBusy(true);
      try {
        await unsubscribeFromPush(token, subscriptionId);
        setMessage("이 기기 알림을 껐어요.");
        await loadNotificationSettings();
      } catch (error) {
        setMessage(errorMessage(error));
      } finally {
        setBusy(false);
      }
    },
    [loadNotificationSettings, token],
  );

  const updatePreferences = useCallback(
    async (patch: Partial<NotificationPreferences>) => {
      if (!notify) return;
      const next = { ...notify.preferences, ...patch };
      setNotify({ ...notify, preferences: next });
      try {
        await saveNotificationPreferences(token, next);
        const changed = Object.keys(patch)[0];
        if (changed) {
          track("push_preference_changed", {
            category: changed,
            enabled: String((patch as Record<string, unknown>)[changed]),
          });
        }
      } catch (error) {
        setMessage(errorMessage(error));
        await loadNotificationSettings();
      }
    },
    [loadNotificationSettings, notify, token],
  );

  const testPush = useCallback(async () => {
    setBusy(true);
    try {
      const queued = await sendTestPush(token);
      setMessage(
        queued > 0
          ? "테스트 알림을 보냈어요. 몇 분 안에 도착합니다."
          : "보낼 기기가 없어요. 먼저 이 기기에서 알림을 켜 주세요.",
      );
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }, [token]);

  useEffect(() => {
    if (tab !== "connect") return;
    void registerServiceWorker();
    void loadNotificationSettings().catch((error) => setMessage(errorMessage(error)));
  }, [loadNotificationSettings, tab]);

  async function run(action: () => Promise<void|string>, success: string) {
    setBusy(true);
    setMessage("");
    try {
      const outcome=await action();
      setMessage(typeof outcome === "string" ? outcome : success);
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function createClient(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    await run(async () => {
      await api(
        "/api/v1/business/clients",
        {
          method: "POST",
          body: JSON.stringify({
            name: data.get("name"),
            phone: data.get("phone"),
            email: data.get("email") || null,
            preferredStyle: data.get("preferredStyle") || null,
            serviceFormula: data.get("serviceFormula") || null,
            privateNotes: data.get("privateNotes") || null,
          }),
        },
        token,
      );
      form.reset();
      await loadClients();
      track("client_created", { source: "manual" });
    }, "고객을 장부에 추가했어요.");
  }

  async function openClient(id: string) {
    setMessage("");
    try {
      const [client, craft] = await Promise.all([
        api<ClientDetail>(`/api/v1/business/clients/${id}`, {}, token),
        api<{ care: CraftCareProfile | null; treatments: TreatmentRecord[] }>(
          `/api/v1/craft/clients/${id}`,
          {},
          token,
        ),
      ]);
      setSelectedClient(client);
      setCraftClient(craft);
    } catch (error) {
      setMessage(errorMessage(error));
    }
  }

  async function saveCareProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedClient) return;
    const data = new FormData(event.currentTarget);
    const consented = data.get("consentRecorded") === "on";
    await run(async () => {
      await api(
        `/api/v1/craft/clients/${selectedClient.client.id}/care`,
        {
          method: "PUT",
          body: JSON.stringify({
            sensitivities: data.get("sensitivities") || null,
            reactionHistory: data.get("reactionHistory") || null,
            patchTestStatus: data.get("patchTestStatus"),
            patchTestAt: data.get("patchTestAt")
              ? seoulIso(data.get("patchTestAt"))
              : null,
            consentScope: consented ? "시술 안전 기록 관리" : null,
            consentRecordedAt: consented
              ? craftClient?.care?.consent_recorded_at || new Date().toISOString()
              : null,
          }),
        },
        token,
      );
      await openClient(selectedClient.client.id);
    }, "시술 전 확인 사항을 저장했어요.");
  }

  async function createTreatmentRecord(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedClient) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    await run(async () => {
      await api(
        `/api/v1/craft/clients/${selectedClient.client.id}/treatments`,
        {
          method: "POST",
          body: JSON.stringify({
            serviceId: data.get("serviceId") || null,
            occurredAt: seoulIso(data.get("occurredAt")),
            formula: data.get("formula") || null,
            processNotes: data.get("processNotes") || null,
            processingMinutes: Number(data.get("processingMinutes")),
            paymentMethod:data.get("paymentMethod"),
            actualPrice: Number(data.get("actualPrice")),
            materialCost: Number(data.get("materialCost")),
            outcome: data.get("outcome"),
            aftercare: data.get("aftercare") || null,
            rebookingWeeks: Number(data.get("rebookingWeeks")),
          }),
        },
        token,
      );
      form.reset();
      await Promise.all([
        openClient(selectedClient.client.id),
        loadCraftOverview(),
      ]);
      track("treatment_record_created", { clientId: selectedClient.client.id });
    }, "시술 기록을 타임라인에 남겼어요.");
  }

  async function createInventoryItem(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    await run(async () => {
      await api(
        "/api/v1/craft/inventory",
        {
          method: "POST",
          body: JSON.stringify({
            name: data.get("name"),
            category: data.get("category"),
            unit: data.get("unit"),
            quantity: Number(data.get("quantity")),
            reorderPoint: Number(data.get("reorderPoint")),
            unitCost: Number(data.get("unitCost")),
          }),
        },
        token,
      );
      form.reset();
      await loadCraftOverview();
    }, "재고 품목을 추가했어요.");
  }

  async function adjustInventory(item: InventoryItem, direction: 1 | -1) {
    const amount = window.prompt(
      direction > 0 ? `${item.name} 입고 수량` : `${item.name} 사용·폐기 수량`,
      "1",
    );
    if (!amount || !Number.isFinite(Number(amount)) || Number(amount) <= 0) return;
    const reason = direction > 0
      ? "restock"
      : window.confirm("폐기·손실로 기록할까요? 취소를 누르면 시술 사용으로 기록합니다.")
        ? "waste"
        : "consume";
    await run(async () => {
      await api(
        `/api/v1/craft/inventory/${item.id}/movements`,
        {
          method: "POST",
          body: JSON.stringify({ delta: Number(amount) * direction, reason }),
        },
        token,
      );
      await loadCraftOverview();
    }, direction > 0 ? "입고 수량을 반영했어요." : "사용 수량을 반영했어요.");
  }

  async function copyRebookingDraft(text: string) {
    await navigator.clipboard.writeText(text);
    setMessage("개인 시술 정보 없이 안부 문구만 복사했어요.");
  }

  async function saveClient(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedClient) return;
    const data = new FormData(event.currentTarget);
    await run(async () => {
      await api(
        `/api/v1/business/clients/${selectedClient.client.id}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            name: data.get("name"),
            phone: data.get("phone"),
            email: data.get("email") || null,
            birthday: data.get("birthday") || null,
            preferredStyle: data.get("preferredStyle") || null,
            serviceFormula: data.get("serviceFormula") || null,
            privateNotes: data.get("privateNotes") || null,
          }),
        },
        token,
      );
      await Promise.all([
        loadClients(clientQuery),
        openClient(selectedClient.client.id),
      ]);
    }, "고객 기록을 저장했어요.");
  }

  async function deleteClient() {
    if (
      !selectedClient ||
      !window.confirm(
        `${selectedClient.client.name} 고객 기록과 비공개 사진을 삭제할까요? 예약 이력은 익명 연결만 해제됩니다.`,
      )
    )
      return;
    await run(async () => {
      await api(
        `/api/v1/business/clients/${selectedClient.client.id}`,
        { method: "DELETE" },
        token,
      );
      setSelectedClient(null);
      await loadClients();
    }, "고객 기록을 삭제했습니다.");
  }

  async function uploadMedia(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedClient) return;
    const data = new FormData(event.currentTarget);
    await run(async () => {
      await apiForm(
        `/api/v1/business/clients/${selectedClient.client.id}/media`,
        data,
        token,
      );
      await openClient(selectedClient.client.id);
      event.currentTarget.reset();
    }, "시술 사진을 저장했어요. 나만 볼 수 있어요.");
  }

  async function deleteMedia(id: string) {
    if (!selectedClient) return;
    await run(async () => {
      await api(
        `/api/v1/business/clients/${selectedClient.client.id}/media/${id}`,
        { method: "DELETE" },
        token,
      );
      await openClient(selectedClient.client.id);
    }, "사진을 삭제했어요.");
  }

  async function createManualBooking(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    await run(async () => {
      const selected = clients.find(
        (client) => client.id === data.get("clientId"),
      );
      const result = await api<{results?:Array<{date:string;id?:string;error?:string}>}>(
        Number(data.get("repeatCount") || 1) > 1 ? "/api/v1/business/calendar/bookings/series" : "/api/v1/business/calendar/bookings",
        {
          method: "POST",
          body: JSON.stringify({
            serviceId: data.get("serviceId"),
            clientId: selected?.id,
            customerName: selected?.name || data.get("customerName"),
            customerPhone: selected?.phone || data.get("customerPhone"),
            customerEmail: selected?.email || data.get("customerEmail") || "",
            customerNotes: data.get("customerNotes") || "",
            scheduledAt: seoulIso(data.get("scheduledAt")),
            customerRequestedBooking: true,
            repeatCount:Number(data.get("repeatCount") || 1),intervalWeeks:Number(data.get("intervalWeeks") || 4),
          }),
        },
        token,
      );
      if(result.results)setSeriesSummary(`${result.results.length}회 중 ${result.results.filter(row=>row.id).length}회 잡았어요. ${result.results.filter(row=>row.error).map(row=>`${dateKey(row.date)}는 예약하지 못했어요`).join(" · ")}`);
      form.reset();
      await Promise.all([loadCalendar(), loadClients(), onChanged()]);
      track("manual_booking_created", {});
      if(result.results)return `${result.results.length}회 중 ${result.results.filter(row=>row.id).length}회 예약을 잡았어요. 실패한 날짜는 위 내용을 확인해 주세요.`;
    }, "예약을 확정으로 추가했어요.");
  }

  async function createBlock(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    await run(async () => {
      await api(
        "/api/v1/business/calendar/blocks",
        {
          method: "POST",
          body: JSON.stringify({
            startsAt: seoulIso(data.get("startsAt")),
            endsAt: seoulIso(data.get("endsAt")),
            kind: data.get("kind"),
            note: data.get("note") || null,
          }),
        },
        token,
      );
      form.reset();
      await loadCalendar();
    }, "예약 못 받는 시간으로 막았어요.");
  }

  async function rescheduleBooking(id: string) {
    const current = bookings.find((booking) => booking.id === id);
    const value = window.prompt(
      "새 시간을 YYYY-MM-DDTHH:mm 형식으로 입력하세요.",
      current
        ? `${dateKey(current.scheduled_at)}T${koTime.format(new Date(current.scheduled_at))}`
        : "",
    );
    if (!value) return;
    await run(async () => {
      await api(
        `/api/v1/business/calendar/bookings/${id}/reschedule`,
        {
          method: "PATCH",
          body: JSON.stringify({ scheduledAt: seoulIso(value) }),
        },
        token,
      );
      await Promise.all([loadCalendar(), onChanged()]);
    }, "예약 시간을 변경했습니다.");
  }

  async function deleteBlock(id: string) {
    await run(async () => {
      await api(
        `/api/v1/business/calendar/blocks/${id}`,
        { method: "DELETE" },
        token,
      );
      await loadCalendar();
    }, "예약 불가 시간을 삭제했습니다.");
  }

  async function previewImport() {
    await run(async () => {
      setPreview(
        await api<ImportPreview>(
          "/api/v1/business/clients/import/preview",
          {
            method: "POST",
            body: JSON.stringify({ mode: importMode, rows: importRows }),
          },
          token,
        ),
      );
    }, "장부에 옮겼을 때 바뀔 내용을 정리했어요.");
  }

  async function applyImport() {
    if (
      !preview ||
      preview.summary.error > 0 ||
      !window.confirm(
        `신규 ${preview.summary.create}명, 갱신 ${preview.summary.update}명을 고객 장부에 적용할까요?`,
      )
    )
      return;
    await run(async () => {
      await api(
        "/api/v1/business/clients/import/apply",
        {
          method: "POST",
          body: JSON.stringify({ mode: importMode, rows: importRows }),
        },
        token,
      );
      setImportRows([]);
      setPreview(null);
      await loadClients();
      track("client_import_applied", { mode: importMode });
    }, "고객 장부를 모두 옮겼어요.");
  }

  function exportClients() {
    downloadCsv("/api/v1/business/clients/export.csv", "hairbridge-clients.csv", "고객");
  }

  function downloadCsv(path: string, filename: string, label: string) {
    void fetch(`${API_URL}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error();
        const url = URL.createObjectURL(await response.blob());
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = filename;
        anchor.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => setMessage(`${label} 파일을 내려받지 못했어요.`));
  }

  async function loadOperationCsv(kind: OperationImport["kind"], file: File) {
    try {
      const text = await file.text();
      setOperationImport(
        kind === "services"
          ? { kind, rows: parseServiceCsv(text) }
          : { kind, rows: parseBookingHistoryCsv(text) },
      );
      setOperationPreview(null);
      setOperationAttested(false);
      setMessage("파일을 읽었어요. 장부에 옮기기 전에 바뀔 내용을 확인해 주세요.");
    } catch {
      setOperationImport(null);
      setOperationPreview(null);
      setMessage("파일의 칸 이름과 값 형식을 확인해 주세요.");
    }
  }

  async function previewOperationImport() {
    if (!operationImport) return;
    if (!operationAttested) {
      setMessage("데이터 관리 권한과 이전 동의를 먼저 확인해 주세요.");
      return;
    }
    await run(async () => {
      setOperationPreview(
        await api<ImportPreview>(
          "/api/v1/business/operations/import/preview",
          {
            method: "POST",
            body: JSON.stringify({
              ...operationImport,
              mode: operationImport.kind === "services" ? "merge" : "skip",
              privacyAttested: operationAttested,
            }),
          },
          token,
        ),
      );
    }, "장부에 옮겼을 때 바뀔 내용을 정리했어요.");
  }

  async function applyOperationImport() {
    if (
      !operationImport ||
      !operationPreview ||
      !operationAttested ||
      operationPreview.summary.error > 0 ||
      !window.confirm(
        `신규 ${operationPreview.summary.create}건, 갱신 ${operationPreview.summary.update}건을 적용할까요?`,
      )
    )
      return;
    await run(async () => {
      await api(
        "/api/v1/business/operations/import/apply",
        {
          method: "POST",
          body: JSON.stringify({
            ...operationImport,
            mode: operationImport.kind === "services" ? "merge" : "skip",
            privacyAttested: operationAttested,
          }),
        },
        token,
      );
      setOperationImport(null);
      setOperationPreview(null);
      setOperationAttested(false);
      await Promise.all([loadClients(), loadCalendar(), onChanged()]);
    }, "시술 메뉴와 지난 예약을 모두 옮겼어요.");
  }

  async function updateWaitlist(id: string, status: "booked" | "withdrawn") {
    await run(async () => {
      await api(
        `/api/v1/business/waitlist/${id}`,
        { method: "PATCH", body: JSON.stringify({ status }) },
        token,
      );
      await loadWaitlist();
    }, status === "booked" ? "예약 완료로 정리했습니다." : "대기 요청을 종료했습니다.");
  }

  async function createFeed() {
    await run(async () => {
      const result = await api<{ url: string }>(
        "/api/v1/business/calendar-feed",
        { method: "POST" },
        token,
      );
      setFeedUrl(result.url);
      await navigator.clipboard.writeText(result.url).catch(() => undefined);
    }, "새 캘린더 주소를 만들어 복사했어요. 이전 주소는 더 이상 열리지 않아요.");
  }

  async function revokeFeed() {
    await run(async () => {
      await api("/api/v1/business/calendar-feed", { method: "DELETE" }, token);
      setFeedUrl("");
    }, "캘린더 구독 주소를 폐기했습니다.");
  }

  const days = Array.from(
    { length: calendarView === "month" ? new Date(Number(calendarDate.slice(0,4)),Number(calendarDate.slice(5,7)),0).getDate() : calendarView === "week" ? 7 : 1 },
    (_, index) => shiftDate(range.start, index),
  );

  return (
    <section
      className="business-workspace"
      aria-labelledby="business-workspace-title"
    >
      <div className="workspace-heading">
        <span>
          <p className="eyebrow">매일 여는 곳</p>
          <h2 id="business-workspace-title">
            오늘 일정과 고객
          </h2>
          <p>
            전화·DM으로 받은 예약까지 한곳에 모으고, 지난 시술과 다음 방문을
            이어갑니다.
          </p>
        </span>
        <div className="workspace-tabs" role="tablist" aria-label="예약과 고객">
          <button
            className={tab === "calendar" ? "active" : ""}
            onClick={() => setTab("calendar")}
          >
            <CalendarDays /> 일정
          </button>
          <button
            className={tab === "clients" ? "active" : ""}
            onClick={() => setTab("clients")}
          >
            <Users /> 고객
          </button>
          <button
            className={tab === "operations" ? "active" : ""}
            onClick={() => setTab("operations")}
          >
            <BadgeDollarSign /> 운영
            {craftOverview?.lowStock ? <b>{craftOverview.lowStock}</b> : null}
          </button>
          <button
            className={tab === "waitlist" ? "active" : ""}
            onClick={() => setTab("waitlist")}
          >
            <BellRing /> 대기
            {waitlist.entries.length > 0 && <b>{waitlist.entries.length}</b>}
          </button>
          <button
            className={tab === "transfer" ? "active" : ""}
            onClick={() => setTab("transfer")}
          >
            <FileSpreadsheet /> 장부 옮기기
          </button>
          <button
            className={tab === "connect" ? "active" : ""}
            onClick={() => setTab("connect")}
          >
            <Link2 /> 알림·캘린더
          </button>
        </div>
      </div>
      {seriesSummary && <p role="status">{seriesSummary}</p>}
      {message && (
        <div className="workspace-message" role="status">
          {message}
        </div>
      )}

      {tab === "calendar" && (
        <div className="workspace-stack">
          <div className="panel calendar-toolbar">
            <label className="field">
              <span>기준 날짜</span>
              <input
                type="date"
                value={calendarDate}
                onChange={(event) => setCalendarDate(event.target.value)}
              />
            </label>
            <div className="workspace-segmented">
              <button
                className={calendarView === "day" ? "active" : ""}
                onClick={() => setCalendarView("day")}
              >
                하루
              </button>
              <button
                className={calendarView === "week" ? "active" : ""}
                onClick={() => setCalendarView("week")}
              >
                주간
              </button>
            </div>
            <button
              className="button button-outline"
              onClick={() => setCalendarDate(todayKey())}
            >
              오늘
            </button>
          </div>
          <button className="button button-outline" aria-pressed={calendarView === "month"} onClick={()=>setCalendarView("month")}>월 달력</button>
          <div className={`calendar-board ${calendarView}`}>
            {days.map((day) => {
              const dayBookings = bookings.filter(
                (booking) => dateKey(booking.scheduled_at) === day,
              );
              const dayBlocks = blocks.filter(
                (block) => dateKey(block.starts_at) === day,
              );
              return (
                <section key={day} className="calendar-day">
                  <h3><button className="text-button" onClick={()=>{setCalendarDate(day);setCalendarView("day");}}>{koDate.format(new Date(`${day}T12:00:00+09:00`))}</button></h3>
                  {dateOverrides.some(item=>item.date===day)&&<small>영업시간 변경</small>}
                  {calendarView === "month" && <p>{dayBookings.length ? `예약 ${dayBookings.length}건` : "빈 날"}</p>}
                  {dayBookings.map((booking) => (
                    <article
                      className="calendar-event booking"
                      key={booking.id}
                    >
                      <b>
                        {koTime.format(new Date(booking.scheduled_at))} ·{" "}
                        {booking.customer_name}
                      </b>
                      <span>
                        {booking.service_name} · {booking.duration_minutes}분
                      </span>
                      <small>
                        {booking.source === "manual"
                          ? "수동 등록"
                          : "온라인 예약"}{" "}
                        · {bookingStatusLabel(booking.status)}
                      </small>
                      {booking.deposit_status === "requested" && <button onClick={()=>void run(async()=>{await api(`/api/v1/business/calendar/bookings/${booking.id}/deposit`,{method:"PATCH"},token);await loadCalendar();},"입금 확인을 기록했어요.")}>입금 확인</button>}
                      {booking.deposit_status === "confirmed" && <small>입금 확인됨</small>}
                      {booking.series_id && <button onClick={()=>{if(window.confirm("이 예약부터 이후 회차를 모두 취소할까요?"))void run(async()=>{await api(`/api/v1/business/calendar/series/${booking.series_id}?from=${encodeURIComponent(booking.scheduled_at)}`,{method:"DELETE"},token);await loadCalendar();},"이후 반복 예약을 취소했어요.");}}>이후 회차 모두 취소</button>}
                      <span className={`consultation-badge ${booking.consultation_status}`}>
                        <ClipboardCheck /> 사전 상담 {booking.consultation_status === "completed" ? "완료" : "대기"}
                      </span>
                      {booking.consultation_status === "completed" && (
                        <details className="consultation-summary">
                          <summary>상담 내용 보기</summary>
                          {booking.consultation_desired_result && <p><b>원하는 결과</b> {booking.consultation_desired_result}</p>}
                          {booking.consultation_recent_chemical_history && <p><b>최근 시술</b> {booking.consultation_recent_chemical_history}</p>}
                          {booking.consultation_reaction_history && <p><b>이전 반응</b> {booking.consultation_reaction_history}</p>}
                        </details>
                      )}
                      {["pending", "confirmed"].includes(booking.status) && (
                        <button
                          onClick={() => void rescheduleBooking(booking.id)}
                        >
                          시간 변경
                        </button>
                      )}
                    </article>
                  ))}
                  {dayBlocks.map((block) => (
                    <article className="calendar-event block" key={block.id}>
                      <b>
                        {koTime.format(new Date(block.starts_at))}–
                        {koTime.format(new Date(block.ends_at))}
                      </b>
                      <span>{block.note || "예약 불가"}</span>
                      <button onClick={() => void deleteBlock(block.id)}>
                        <Trash2 /> 삭제
                      </button>
                    </article>
                  ))}
                  {dayBookings.length + dayBlocks.length === 0 && (
                    <p className="calendar-empty">일정 없음</p>
                  )}
                </section>
              );
            })}
          </div>
          <div className="dashboard-grid workspace-forms">
            <form className="panel" onSubmit={createManualBooking}>
              <div className="panel-title">
                <span>
                  <h2>수동 예약 추가</h2>
                  <p>전화·DM 예약은 고객 요청을 확인한 뒤 등록하세요.</p>
                </span>
              </div>
              <label className="field">
                <span>기존 고객(선택)</span>
                <select name="clientId" defaultValue="">
                  <option value="">새 고객 정보 입력</option>
                  {clients.filter(client=>!tagFilter || JSON.parse(client.tags||"[]").includes(tagFilter)).map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name} · {client.phone}
                    </option>
                  ))}
                </select>
              </label>
              <div className="form-grid">
                <Field
                  label="고객명"
                  name="customerName"
                  placeholder="기존 고객 선택 시 생략 가능"
                />
                <Field
                  label="연락처"
                  name="customerPhone"
                  placeholder="010-0000-0000"
                />
              </div>
              <Field label="이메일(선택)" name="customerEmail" type="email" />
              <label className="field">
                <span>서비스</span>
                <select name="serviceId" required defaultValue="">
                  <option value="" disabled>
                    서비스 선택
                  </option>
                  {services
                    .filter((service) => service.is_active)
                    .map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.name} · {service.duration_minutes}분
                      </option>
                    ))}
                </select>
              </label>
              <Field
                label="예약 일시(한국시간)"
                name="scheduledAt"
                type="datetime-local"
                step="1800"
                required
              />
              <Field label="요청 메모" name="customerNotes" maxLength={500} />
              <label className="consent compact">
                <input type="checkbox" required />
                <span>
                  고객이 예약을 요청했고 연락처를 예약 관리에 사용하는 데
                  동의했음을 확인합니다.
                </span>
              </label>
              <div className="form-grid"><label className="field"><span>반복 횟수</span><input name="repeatCount" type="number" min={1} max={12} defaultValue={1}/></label><label className="field"><span>몇 주마다</span><input name="intervalWeeks" type="number" min={1} max={12} defaultValue={4}/></label></div>
            <button className="button" disabled={busy}>
                <Plus /> 확정 예약 추가
              </button>
            </form>
            <form className="panel" onSubmit={createBlock}>
              <div className="panel-title">
                <span>
                  <h2>예약 불가 시간</h2>
                  <p>외부 일정·휴무 시간을 30분 단위로 막습니다.</p>
                </span>
              </div>
              <div className="form-grid">
                <Field
                  label="시작(한국시간)"
                  name="startsAt"
                  type="datetime-local"
                  step="1800"
                  required
                />
                <Field
                  label="종료(한국시간)"
                  name="endsAt"
                  type="datetime-local"
                  step="1800"
                  required
                />
              </div>
              <label className="field">
                <span>구분</span>
                <select name="kind" defaultValue="personal">
                  <option value="personal">개인 일정</option>
                  <option value="holiday">휴무</option>
                  <option value="other">기타</option>
                </select>
              </label>
              <Field label="메모" name="note" maxLength={300} />
              <button className="button button-outline" disabled={busy}>
                <Clock3 /> 시간 막기
              </button>
            </form>
          </div>
        </div>
      )}

      {tab === "clients" && (
        <div className="client-workspace">
          <aside className="panel client-list-panel">
            <ContactQueue token={token} kind="birthday" />
            <div className="panel-title">
              <span>
                <h2>고객 장부</h2>
                <p>{clients.length}명 · 디자이너에게만 표시</p>
              </span>
            </div>
            <form
              className="client-search"
              onSubmit={(event) => {
                event.preventDefault();
                void loadClients(clientQuery);
              }}
            >
              <Search />
              <input
                aria-label="고객 검색"
                value={clientQuery}
                onChange={(event) => setClientQuery(event.target.value)}
                placeholder="이름·연락처·이메일"
              />
            </form>
            <label className="field"><span>태그로 묶어 보기</span><input value={tagFilter} onChange={event=>setTagFilter(event.target.value)} placeholder="예: 탈색"/></label>
            <div className="client-list">
              {clients.map((client) => (
                <button
                  key={client.id}
                  className={
                    selectedClient?.client.id === client.id ? "active" : ""
                  }
                  onClick={() => void openClient(client.id)}
                >
                  <b>{client.name}</b>
                  <span>{client.phone}</span>
                  <small>
                    예약 {Number(client.booking_count || 0)}건
                    {client.last_visit_at
                      ? ` · 최근 ${koDate.format(new Date(client.last_visit_at))}`
                      : ""}
                  </small>
                </button>
              ))}
              {clients.length === 0 && (
                <p className="calendar-empty">등록된 고객이 없습니다.</p>
              )}
            </div>
          </aside>
          <div className="workspace-stack">
            {selectedClient ? (
              <section className="panel" key={selectedClient.client.id}>
                <div className="panel-title">
                  <span>
                    <h2>{selectedClient.client.name} 고객 기록</h2>
                    <button className="text-button" onClick={()=>void run(async()=>{await api(`/api/v1/business/rules/blocked/${selectedClient.client.id}`,{method:"PUT",body:JSON.stringify({blocked:true})},token);},"이 연락처의 온라인 예약을 막았어요.")}>이 연락처 예약 막기</button>
                    <button className="text-button" onClick={()=>void run(async()=>{await api(`/api/v1/business/rules/blocked/${selectedClient.client.id}`,{method:"PUT",body:JSON.stringify({blocked:false})},token);},"온라인 예약을 다시 받을 수 있어요.")}>막기 풀기</button>
                    <p>예약 이력과 별도로 디자이너만 보는 비공개 메모입니다.</p>
                  </span>
                  <button
                    className="icon-danger"
                    onClick={() => void deleteClient()}
                    aria-label="고객 삭제"
                  >
                    <Trash2 />
                  </button>
                </div>
                <form onSubmit={saveClient}>
                  <div className="form-grid">
                    <Field
                      label="이름"
                      name="name"
                      defaultValue={selectedClient.client.name}
                      required
                    />
                    <Field
                      label="연락처"
                      name="phone"
                      defaultValue={selectedClient.client.phone}
                      required
                    />
                  </div>
                  <div className="form-grid">
                    <Field
                      label="이메일"
                      name="email"
                      type="email"
                      defaultValue={selectedClient.client.email || ""}
                    />
                    <Field
                      label="생일"
                      name="birthday"
                      type="date"
                      defaultValue={selectedClient.client.birthday || ""}
                    />
                  </div>
                  <label className="field">
                    <span>선호 스타일</span>
                    <textarea
                      name="preferredStyle"
                      rows={2}
                      defaultValue={selectedClient.client.preferred_style || ""}
                    />
                  </label>
                  <label className="field">
                    <span>시술식·약제</span>
                    <textarea
                      name="serviceFormula"
                      rows={3}
                      defaultValue={selectedClient.client.service_formula || ""}
                    />
                  </label>
                  <label className="field">
                    <span>비공개 메모</span>
                    <textarea
                      name="privateNotes"
                      rows={3}
                      defaultValue={selectedClient.client.private_notes || ""}
                    />
                  </label>
                  <button className="button" disabled={busy}>
                    고객 기록 저장
                  </button>
                </form>
                <div className="craft-section-heading">
                  <span>
                    <p className="eyebrow">시술 전</p>
                    <h3>안전 확인과 동의</h3>
                  </span>
                  <small>진단이 아닌 고객이 말한 사실만 기록합니다.</small>
                </div>
                <ClientExtensions token={token} clientId={selectedClient.client.id} onChanged={()=>loadClients(clientQuery)} />
                <form className="craft-care-form" onSubmit={saveCareProfile}>
                  <div className="form-grid">
                    <label className="field">
                      <span>민감 반응·피해야 할 성분</span>
                      <textarea
                        name="sensitivities"
                        rows={2}
                        defaultValue={craftClient?.care?.sensitivities || ""}
                        placeholder="고객이 직접 말한 사실만 기록"
                      />
                    </label>
                    <label className="field">
                      <span>이전 반응과 회복 경과</span>
                      <textarea
                        name="reactionHistory"
                        rows={2}
                        defaultValue={craftClient?.care?.reaction_history || ""}
                      />
                    </label>
                  </div>
                  <div className="form-grid">
                    <label className="field">
                      <span>패치 테스트</span>
                      <select
                        name="patchTestStatus"
                        defaultValue={craftClient?.care?.patch_test_status || "not_needed"}
                      >
                        <option value="not_needed">불필요 확인</option>
                        <option value="needed">필요</option>
                        <option value="scheduled">예정</option>
                        <option value="passed">이상 없음</option>
                        <option value="reaction">반응 있음·시술 재확인</option>
                      </select>
                    </label>
                    <Field
                      label="확인 일시(선택)"
                      name="patchTestAt"
                      type="datetime-local"
                      defaultValue={
                        craftClient?.care?.patch_test_at
                          ? new Date(new Date(craftClient.care.patch_test_at).getTime() + 9 * 3600000)
                              .toISOString()
                              .slice(0, 16)
                          : ""
                      }
                    />
                  </div>
                  <label className="consent compact">
                    <input
                      type="checkbox"
                      name="consentRecorded"
                      defaultChecked={Boolean(craftClient?.care?.consent_recorded_at)}
                    />
                    <span>고객에게 이 안전 기록의 보관 목적과 삭제 방법을 알렸습니다.</span>
                  </label>
                  <button className="button button-outline" disabled={busy}>
                    <ClipboardCheck /> 확인 사항 저장
                  </button>
                </form>

                <div className="craft-section-heading">
                  <span>
                    <p className="eyebrow">시술 후</p>
                    <h3>덮어쓰지 않는 시술 타임라인</h3>
                  </span>
                  <small>처방·비용·결과를 방문별로 남깁니다.</small>
                </div>
                <form className="treatment-form" onSubmit={createTreatmentRecord}>
                  {craftClient?.treatments[0] && <button type="button" className="button button-outline" onClick={event=>{const form=event.currentTarget.form;const record=craftClient.treatments[0];if(!form||!record)return;const values={formula:record.formula,processNotes:record.process_notes,processingMinutes:record.processing_minutes,serviceId:record.service_id,actualPrice:record.actual_price,materialCost:record.material_cost,outcome:record.outcome,aftercare:record.aftercare,rebookingWeeks:record.rebooking_weeks};Object.entries(values).forEach(([name,value])=>{const input=form.elements.namedItem(name);if(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement || input instanceof HTMLSelectElement)input.value=String(value??"");});}}>지난 기록 불러오기</button>}
                  <label className="field"><span>결제 수단</span><select name="paymentMethod"><option value="unknown">미기록</option><option value="card">카드</option><option value="cash">현금</option><option value="transfer">계좌</option><option value="pass">회원권</option></select></label>
                  <div className="form-grid three">
                    <Field
                      label="시술 일시"
                      name="occurredAt"
                      type="datetime-local"
                      defaultValue={seoulInputNow()}
                      required
                    />
                    <label className="field">
                      <span>서비스</span>
                      <select name="serviceId" defaultValue="">
                        <option value="">직접 시술</option>
                        {services.filter((service) => service.is_active).map((service) => (
                          <option key={service.id} value={service.id}>{service.name}</option>
                        ))}
                      </select>
                    </label>
                    <Field
                      label="총 소요(분)"
                      name="processingMinutes"
                      type="number"
                      min="0"
                      max="480"
                      defaultValue="60"
                      required
                    />
                  </div>
                  <label className="field">
                    <span>실제 배합·도포·기법</span>
                    <textarea name="formula" rows={3} placeholder="예: 7N 40g + 7A 20g, 산화제 6%" />
                  </label>
                  <label className="field">
                    <span>과정 메모</span>
                    <textarea name="processNotes" rows={2} placeholder="방치 시간, 커트 기준점, 현장 조정" />
                  </label>
                  <div className="form-grid three">
                    <Field label="실결제액" name="actualPrice" type="number" min="0" defaultValue="0" required />
                    <Field label="사용 약제 원가" name="materialCost" type="number" min="0" defaultValue="0" required />
                    <label className="field">
                      <span>결과</span>
                      <select name="outcome" defaultValue="as_planned">
                        <option value="as_planned">계획대로</option>
                        <option value="adjusted">현장 조정</option>
                        <option value="follow_up_needed">후속 확인 필요</option>
                      </select>
                    </label>
                  </div>
                  <div className="form-grid">
                    <label className="field">
                      <span>고객에게 안내한 홈케어</span>
                      <textarea name="aftercare" rows={2} />
                    </label>
                    <Field label="다음 관리 주기(주)" name="rebookingWeeks" type="number" min="1" max="52" defaultValue="6" required />
                  </div>
                  <button className="button" disabled={busy}>시술 기록 남기기</button>
                </form>

                <div className="treatment-timeline">
                  {craftClient?.treatments.map((record) => (
                    <article key={record.id}>
                      <div>
                        <b>{koDate.format(new Date(record.occurred_at))} · {record.service_name}</b>
                        <small>
                          {record.processing_minutes}분 · 실결제 {won(record.actual_price)} · 약제 {won(record.material_cost)}
                        </small>
                      </div>
                      <span className={`outcome ${record.outcome}`}>
                        {record.outcome === "as_planned" ? "계획대로" : record.outcome === "adjusted" ? "현장 조정" : "후속 확인"}
                      </span>
                      {record.formula && <p><b>배합·기법</b> {record.formula}</p>}
                      {record.process_notes && <p><b>과정</b> {record.process_notes}</p>}
                      {record.aftercare && <p><b>홈케어</b> {record.aftercare}</p>}
                      <small>다음 관리 기준 {record.rebooking_weeks}주</small>
                    </article>
                  ))}
                  {craftClient?.treatments.length === 0 && (
                    <p className="calendar-empty">첫 시술 기록을 남기면 이전 처방과 결과를 바로 비교할 수 있어요.</p>
                  )}
                </div>
                <div className="client-history">
                  <h3>예약 이력</h3>
                  {selectedClient.history.map((item) => (
                    <div key={item.id}>
                      <b>
                        {koDate.format(new Date(item.scheduled_at))} ·{" "}
                        {item.service_name}
                      </b>
                      <span>{bookingStatusLabel(item.status)}</span>
                    </div>
                  ))}
                  {selectedClient.history.length === 0 && (
                    <p>연결된 예약이 없습니다.</p>
                  )}
                </div>
                <form className="media-upload" onSubmit={uploadMedia}>
                  <h3>비공개 시술 사진</h3>
                  <input
                    aria-label="시술 사진"
                    type="file"
                    name="file"
                    accept="image/jpeg,image/png,image/webp"
                    required
                  />
                  <Field label="사진 메모" name="caption" maxLength={200} />
                  <label className="field"><span>사진 종류</span><select name="mediaRole"><option value="">단독 사진</option><option value="before">시술 전</option><option value="after">시술 후</option></select></label>
                  <button className="button button-outline" disabled={busy}>
                    <Upload /> 사진 저장
                  </button>
                </form>
                <p>{selectedClient.media.length < 2 ? "사진 두 장이 있으면 나란히 볼 수 있어요." : "비교할 두 장을 골라 주세요."}</p>
                {mediaSelection.length>0&&<button className="text-button" onClick={()=>setMediaSelection([])}>사진 선택 풀기</button>}
                <div className="client-media-grid">
                  {selectedClient.media.filter(item=>mediaSelection.length!==2||mediaSelection.includes(item.id)).map((item) => (
                    <figure key={item.id}>
                      <AuthenticatedImage
                        path={item.asset_path}
                        token={token}
                        alt={item.caption || "비공개 시술 사진"}
                      />
                      <figcaption>{item.caption || "비공개 시술 사진"} · {koDate.format(new Date(item.created_at))}</figcaption>
                      <label><input type="checkbox" checked={mediaSelection.includes(item.id)} onChange={event=>setMediaSelection(current=>event.target.checked?[...current,item.id].slice(-2):current.filter(id=>id!==item.id))}/>나란히 보기</label>
                      <button onClick={() => void deleteMedia(item.id)}>
                        <Trash2 /> 삭제
                      </button>
                    </figure>
                  ))}
                </div>
              </section>
            ) : (
              <form className="panel" onSubmit={createClient}>
                <div className="panel-title">
                  <span>
                    <h2>새 고객 추가</h2>
                    <p>최소 이름과 연락처로 시작할 수 있습니다.</p>
                  </span>
                </div>
                <div className="form-grid">
                  <Field label="이름" name="name" required />
                  <Field
                    label="연락처"
                    name="phone"
                    required
                    placeholder="010-0000-0000"
                  />
                </div>
                <Field label="이메일" name="email" type="email" />
                <Field label="선호 스타일" name="preferredStyle" />
                <Field label="시술식·약제" name="serviceFormula" />
                <Field label="비공개 메모" name="privateNotes" />
                <button className="button" disabled={busy}>
                  <Plus /> 고객 추가
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {tab === "operations" && (
        <div className="workspace-stack craft-operations">
          <section className="panel operations-hero">
            <div className="panel-title">
              <span>
                <p className="eyebrow">운영이 남는 장부</p>
                <h2>매출이 아니라 실제로 남은 시술을 봅니다</h2>
                <p>시술 기록의 실결제·약제 원가·소요 시간을 기준으로 계산합니다.</p>
              </span>
              <div className="workspace-segmented">
                <button
                  className={craftRange === 30 ? "active" : ""}
                  onClick={() => setCraftRange(30)}
                >30일</button>
                <button
                  className={craftRange === 90 ? "active" : ""}
                  onClick={() => setCraftRange(90)}
                >90일</button>
              </div>
            </div>
            <div className="craft-metrics" aria-label={`${craftRange}일 운영 지표`}>
              <article><small>시술 기록</small><b>{craftOverview?.metrics.treatments || 0}건</b></article>
              <article><small>실결제 합계</small><b>{won(craftOverview?.metrics.revenue || 0)}</b></article>
              <article className="accent"><small>약제 제외 기여액</small><b>{won(craftOverview?.metrics.contribution || 0)}</b><em>{craftOverview?.metrics.contributionRate || 0}%</em></article>
              <article><small>시간당 실결제</small><b>{won(craftOverview?.metrics.revenuePerHour || 0)}</b></article>
            </div>
            <p className="provider-wait">세금·임대료·인건비 전의 시술 기여액입니다. 회계상 순이익으로 표시하지 않습니다.</p>
          </section>

          <div className="dashboard-grid craft-grid">
            <section className="panel rebooking-board">
              <div className="panel-title">
                <span>
                  <h2>다시 만날 때가 된 고객</h2>
                  <p>마지막 시술에서 정한 주기로 30일 안의 고객을 보여줍니다.</p>
                </span>
              </div>
              <div className="rebooking-list">
                {craftOverview?.rebooking.map((entry) => (
                  <article key={entry.client_id}>
                    <div>
                      <b>{entry.client_name} · {entry.service_name}</b>
                      <small>
                        {entry.overdueDays > 0
                          ? `${entry.overdueDays}일 지남`
                          : `${koDate.format(new Date(entry.dueAt))} 예정`}
                        {" · "}{entry.phone}
                      </small>
                      <p>{entry.draft}</p>
                    </div>
                    <div className="button-row compact">
                      <button className="button button-outline" onClick={() => void copyRebookingDraft(entry.draft)}>
                        문구 복사
                      </button>
                      <button className="button button-quiet" onClick={() => { setTab("clients"); void openClient(entry.client_id); }}>
                        기록 보기
                      </button>
                    </div>
                  </article>
                ))}
                {!craftOverview?.rebooking.length && (
                  <p className="calendar-empty">시술 기록에 다음 관리 주기를 남기면 자동으로 모입니다.</p>
                )}
              </div>
            </section>

            <section className="panel inventory-board">
              <div className="panel-title">
                <span>
                  <h2>약제·소모품 재고</h2>
                  <p>부족해진 뒤가 아니라 발주 기준에 닿을 때 알려드립니다.</p>
                </span>
                {craftOverview?.lowStock ? <strong className="stock-alert">발주 {craftOverview.lowStock}</strong> : null}
              </div>
              <div className="inventory-list">
                {craftOverview?.inventory.map((item) => {
                  const low = Number(item.quantity) <= Number(item.reorder_point);
                  return (
                    <article key={item.id} className={low ? "low" : ""}>
                      <span>
                        <b>{item.name}</b>
                        <small>{item.category} · {won(item.unit_cost)}/{item.unit}</small>
                      </span>
                      <strong>{item.quantity}{item.unit}</strong>
                      <small>발주 기준 {item.reorder_point}{item.unit}</small>
                      <div className="inventory-actions">
                        <button onClick={() => void adjustInventory(item, 1)}>입고</button>
                        <button onClick={() => void adjustInventory(item, -1)}>사용</button>
                      </div>
                    </article>
                  );
                })}
                {!craftOverview?.inventory.length && (
                  <p className="calendar-empty">자주 쓰는 약제부터 등록해 보세요.</p>
                )}
              </div>
              <form className="inventory-create" onSubmit={createInventoryItem}>
                <h3><PackageOpen /> 재고 품목 추가</h3>
                <div className="form-grid three">
                  <Field label="품목명" name="name" required />
                  <Field label="분류" name="category" defaultValue="약제" required />
                  <Field label="단위" name="unit" defaultValue="개" required />
                </div>
                <div className="form-grid three">
                  <Field label="현재 수량" name="quantity" type="number" min="0" step="0.1" defaultValue="0" required />
                  <Field label="발주 기준" name="reorderPoint" type="number" min="0" step="0.1" defaultValue="1" required />
                  <Field label="단위 원가" name="unitCost" type="number" min="0" defaultValue="0" required />
                </div>
                <button className="button button-outline" disabled={busy}><Plus /> 품목 추가</button>
              </form>
            </section>
          </div>
        </div>
      )}

      {tab === "waitlist" && (
        <>
        <ContactQueue token={token} kind="waitlist" />
        <section className="panel waitlist-panel">
          <div className="panel-title">
            <span>
              <h2>취소 자리 대기 손님</h2>
              <p>
                같은 서비스·희망일·시간대의 고객만 보여줍니다. 빈 시간은 먼저
                예약한 고객에게 확정되며 자동 예약되지 않습니다.
              </p>
            </span>
            <button className="button button-outline" onClick={() => void loadWaitlist()}>
              새로고침
            </button>
          </div>
          {!waitlist.deliveryEnabled && (
            <p className="provider-wait">
              이메일 실발송이 꺼져 있습니다. `알림 준비` 상태 고객은 아래 연락처로
              직접 안내하세요.
            </p>
          )}
          <div className="waitlist-list">
            {waitlist.entries.map((entry) => (
              <article key={entry.id}>
                <span>
                  <b>{entry.customer_name} · {entry.service_name}</b>
                  <small>
                    {entry.desired_date} · {entry.time_window === "any" ? "시간 무관" : entry.time_window}
                    {entry.note ? ` · ${entry.note}` : ""}
                  </small>
                  <small>{entry.customer_email || entry.customer_phone}</small>
                </span>
                <em>{entry.status === "notified" ? "알림 준비" : "대기 중"}</em>
                <div className="button-row">
                  <button onClick={() => void updateWaitlist(entry.id, "booked")}>예약 완료</button>
                  <button onClick={() => void updateWaitlist(entry.id, "withdrawn")}>종료</button>
                </div>
              </article>
            ))}
            {waitlist.entries.length === 0 && (
              <p className="calendar-empty">현재 활성 대기 요청이 없습니다.</p>
            )}
          </div>
        </section>
        </>
      )}

      {tab === "transfer" && (
        <div className="workspace-stack">
        <section className="panel transfer-panel">
          <div className="panel-title">
            <span>
              <h2>고객 장부 옮기기</h2>
              <p>
                쓰던 고객 장부를 불러오기 전에 겹치는 연락처와 바뀔 내용을 먼저
                보여드립니다.
              </p>
              <p className="transfer-note">
                한 번에 최대 500명까지 옮길 수 있으며, 이름과 연락처가 필요합니다.
                파일 형식은 CSV입니다.
              </p>
            </span>
            <button className="button button-outline" onClick={exportClients}>
              <Download /> 고객 장부 백업 받기
            </button>
          </div>
          <ClientCsvPicker onRows={rows => { setImportRows(rows); setPreview(null); }} />
          {switchRows.get().length > 0 && <button className="button" onClick={() => { setImportRows(switchRows.get()); setPreview(null); }}>가입 전 시험한 {switchRows.get().length}명 이어서 옮기기</button>}
          {importRows.length > 0 && (
            <div className="import-preview">
              <div className="import-controls">
                <label>
                  <input
                    type="radio"
                    checked={importMode === "skip"}
                    onChange={() => {
                      setImportMode("skip");
                      setPreview(null);
                    }}
                  />{" "}
                  기존 연락처 건너뛰기
                </label>
                <label>
                  <input
                    type="radio"
                    checked={importMode === "merge"}
                    onChange={() => {
                      setImportMode("merge");
                      setPreview(null);
                    }}
                  />{" "}
                  기존 기록 덮어쓰기
                </label>
                <button
                  className="button"
                  onClick={() => void previewImport()}
                  disabled={busy}
                >
                  바뀔 내용 확인하기
                </button>
              </div>
              {preview && (
                <>
                  <div className="import-summary">
                    <span>
                      신규 <b>{preview.summary.create}</b>
                    </span>
                    <span>
                      갱신 <b>{preview.summary.update}</b>
                    </span>
                    <span>
                      건너뜀 <b>{preview.summary.skip}</b>
                    </span>
                    <span>
                      오류 <b>{preview.summary.error}</b>
                    </span>
                  </div>
                  <div className="import-rows">
                    {preview.rows.slice(0, 20).map((row) => (
                      <span key={row.rowId}>
                        행 {row.rowId}: {row.action}
                        {row.error ? ` · ${row.error}` : ""}
                      </span>
                    ))}
                  </div>
                  <button
                    className="button"
                    onClick={() => void applyImport()}
                    disabled={busy || preview.summary.error > 0}
                  >
                    확인한 내용 옮기기
                  </button>
                </>
              )}
            </div>
          )}
        </section>
        <section className="panel transfer-panel">
          <div className="panel-title">
            <span>
              <h2>시술 메뉴와 지난 예약 옮기기</h2>
              <p>
                서비스는 같은 이름으로 병합하고, 과거 예약은 기존 서비스와 고객
                연락처를 연결합니다. 미래 예약은 이 경로에서 만들지 않습니다.
              </p>
            </span>
            <div className="button-row">
              <button
                className="button button-outline"
                onClick={() => downloadCsv("/api/v1/business/services/export.csv", "hairbridge-services.csv", "서비스")}
              >
                시술 메뉴 백업 받기
              </button>
              <button
                className="button button-outline"
                onClick={() => downloadCsv("/api/v1/business/booking-history/export.csv", "hairbridge-booking-history.csv", "예약 이력")}
              >
                지난 예약 백업 받기
              </button>
            </div>
          </div>
          <div className="transfer-grid">
            <label className="csv-drop">
              <FileSpreadsheet />
              <b>시술 메뉴 파일 선택</b>
              <span>서비스명, 가격, 소요시간(분), 재예약주기(주), 상담질문</span>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void loadOperationCsv("services", file);
                }}
              />
            </label>
            <label className="csv-drop">
              <FileSpreadsheet />
              <b>지난 예약 파일 선택</b>
              <span>서비스명, 고객명, 연락처, 예약일시, 상태, 유입경로</span>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void loadOperationCsv("booking_history", file);
                }}
              />
            </label>
          </div>
          {operationImport && (
            <div className="import-preview">
              <p>
                {operationImport.kind === "services" ? "서비스" : "과거 예약"} {operationImport.rows.length}행
              </p>
              <label className="consent compact">
                <input
                  type="checkbox"
                  checked={operationAttested}
                  onChange={(event) => {
                    setOperationAttested(event.target.checked);
                    setOperationPreview(null);
                  }}
                />
                <span>
                  이 데이터의 관리 권한과 고객 연락처 이전에 필요한 동의를 확인했습니다.
                </span>
              </label>
              <button className="button" onClick={() => void previewOperationImport()} disabled={busy}>
                바뀔 내용 확인하기
              </button>
              {operationPreview && (
                <>
                  <div className="import-summary">
                    <span>신규 <b>{operationPreview.summary.create}</b></span>
                    <span>갱신 <b>{operationPreview.summary.update}</b></span>
                    <span>건너뜀 <b>{operationPreview.summary.skip}</b></span>
                    <span>오류 <b>{operationPreview.summary.error}</b></span>
                  </div>
                  <div className="import-rows">
                    {operationPreview.rows.slice(0, 20).map((row) => (
                      <span key={row.rowId}>행 {row.rowId}: {row.action}{row.error ? ` · ${row.error}` : ""}</span>
                    ))}
                  </div>
                  <button
                    className="button"
                    onClick={() => void applyOperationImport()}
                    disabled={busy || operationPreview.summary.error > 0}
                  >
                    확인한 내용 옮기기
                  </button>
                </>
              )}
            </div>
          )}
        </section>
        </div>
      )}

      {tab === "connect" && (
        <div className="workspace-stack">
          <section className="panel notify-panel">
            <div className="panel-title">
              <span>
                <h2>앱을 열지 않아도 알림 받기</h2>
                <p>
                  새 예약과 취소를 이 기기 알림으로 받습니다. 고객에게는
                  아무것도 보내지 않습니다.
                </p>
              </span>
              {notify?.configured && notify.subscriptions.length > 0 && (
                <button
                  className="button button-outline"
                  onClick={() => void testPush()}
                  disabled={busy}
                >
                  테스트 알림
                </button>
              )}
            </div>

            {!notify && <p className="calendar-empty">알림 설정을 불러오는 중이에요.</p>}

            {notify && !notify.configured && (
              <div className="connect-notice">
                <BellRing />
                <span>
                  <b>알림 채널이 아직 연결되지 않았어요</b>
                  <small>
                    발송 키가 설정되기 전까지는 알림을 켤 수 없습니다. 이
                    화면은 준비되면 바로 열립니다.
                  </small>
                </span>
              </div>
            )}

            {notify?.configured && notifyCapability.state === "needs_home_screen" && (
              <div className="connect-notice">
                <BellRing />
                <span>
                  <b>홈 화면에 추가하면 알림을 받을 수 있어요</b>
                  <small>
                    아이폰·아이패드는 공유 버튼 → &quot;홈 화면에 추가&quot;로
                    설치한 뒤 그 아이콘으로 열어야 알림이 동작합니다.
                  </small>
                </span>
              </div>
            )}

            {notify?.configured && notifyCapability.state === "denied" && (
              <div className="connect-notice">
                <BellRing />
                <span>
                  <b>브라우저에서 알림이 차단돼 있어요</b>
                  <small>
                    주소창 옆 자물쇠 → 알림을 &quot;허용&quot;으로 바꾼 뒤 다시
                    시도해 주세요.
                  </small>
                </span>
              </div>
            )}

            {notify?.configured && notifyCapability.state === "unsupported" && (
              <div className="connect-notice">
                <BellRing />
                <span>
                  <b>이 브라우저는 알림을 지원하지 않아요</b>
                  <small>최신 Chrome·Safari·Edge에서 다시 열어 주세요.</small>
                </span>
              </div>
            )}

            {notify?.configured && (
              <div className="notify-devices">
                {notify.subscriptions.map((subscription) => (
                  <article key={subscription.id}>
                    <span>
                      <b>이 계정에 연결된 기기</b>
                      <small>{subscription.user_agent || "기기 정보 없음"}</small>
                      <small>
                        {subscription.last_success_at
                          ? `마지막 도착 ${koDate.format(new Date(subscription.last_success_at))}`
                          : "아직 도착한 알림이 없어요"}
                      </small>
                    </span>
                    <button
                      className="button button-quiet"
                      onClick={() => void disablePush(subscription.id)}
                      disabled={busy}
                    >
                      끄기
                    </button>
                  </article>
                ))}
                {notify.subscriptions.length === 0 && (
                  <p className="calendar-empty">
                    아직 알림을 받는 기기가 없어요.
                  </p>
                )}
                {notifyCapability.state === "ready" && (
                  <button
                    className="button"
                    onClick={() => void enablePush()}
                    disabled={busy}
                  >
                    이 기기로 알림 받기
                  </button>
                )}
              </div>
            )}

            {notify?.configured && (
              <div className="notify-preferences">
                <h3>무엇을 받을지</h3>
                {(
                  [
                    ["bookingCreated", "새 예약이 들어왔을 때"],
                    ["bookingCancelled", "고객이 취소했을 때"],
                    ["waitlistSlotOpen", "대기자에게 돌아갈 자리가 생겼을 때"],
                    ["rebookingDue", "재방문할 때가 된 고객이 생겼을 때"],
                    ["dayDigest", "아침에 오늘 예약 요약"],
                  ] as Array<[keyof NotificationPreferences, string]>
                ).map(([key, label]) => (
                  <label key={key} className="notify-toggle">
                    <input
                      type="checkbox"
                      checked={Boolean(notify.preferences[key])}
                      disabled={!notify.preferences.pushEnabled}
                      onChange={(event) =>
                        void updatePreferences({ [key]: event.target.checked })
                      }
                    />
                    <span>{label}</span>
                  </label>
                ))}
                <label className="notify-toggle">
                  <input
                    type="checkbox"
                    checked={notify.preferences.pushEnabled}
                    onChange={(event) =>
                      void updatePreferences({ pushEnabled: event.target.checked })
                    }
                  />
                  <span>알림 전체 켜기</span>
                </label>
                <div className="notify-hours">
                  <label>
                    <span>방해 금지 시작</span>
                    <select
                      value={notify.preferences.quietStartHour}
                      onChange={(event) =>
                        void updatePreferences({
                          quietStartHour: Number(event.target.value),
                        })
                      }
                    >
                      {HOUR_OPTIONS}
                    </select>
                  </label>
                  <label>
                    <span>방해 금지 끝</span>
                    <select
                      value={notify.preferences.quietEndHour}
                      onChange={(event) =>
                        void updatePreferences({
                          quietEndHour: Number(event.target.value),
                        })
                      }
                    >
                      {HOUR_OPTIONS}
                    </select>
                  </label>
                  <label>
                    <span>아침 요약 시각</span>
                    <select
                      value={notify.preferences.digestHour}
                      onChange={(event) =>
                        void updatePreferences({
                          digestHour: Number(event.target.value),
                        })
                      }
                    >
                      {HOUR_OPTIONS}
                    </select>
                  </label>
                </div>
                <p className="provider-wait">
                  방해 금지 시간에 생긴 알림은 버리지 않고 끝나는 시각에
                  모아서 보냅니다.
                </p>
              </div>
            )}
          </section>
          <section className="panel connect-panel">
            <div className="panel-title">
              <span>
                <h2>외부 캘린더에서 예약 보기</h2>
                <p>
                  Google Calendar, Apple Calendar, Outlook 등에 읽기 전용 구독
                  주소를 추가할 수 있습니다.
                </p>
              </span>
            </div>
            <div className="connect-notice">
              <Link2 />
              <span>
                <b>개인 구독 주소</b>
                <small>
                  주소를 아는 사람은 예약 고객명과 서비스명을 볼 수 있습니다.
                  노출되었다면 즉시 새 주소를 발급하세요.
                </small>
              </span>
            </div>
            {feedUrl && <code>{feedUrl}</code>}
            <div className="button-row">
              <button
                className="button"
                onClick={() => void createFeed()}
                disabled={busy}
              >
                {feedUrl ? "새 주소 재발급" : "구독 주소 발급"}
              </button>
              <button
                className="button button-danger"
                onClick={() => void revokeFeed()}
                disabled={busy}
              >
                주소 폐기
              </button>
            </div>
            <p className="provider-wait">
              Google·네이버 양방향 연결은 외부 캘린더 연결 승인이 끝난 뒤
              제공합니다. 지금 발급하는 주소는 HairBridge 예약을 외부 캘린더에서
              읽기만 할 수 있습니다.
            </p>
          </section>
          <section className="panel reminder-panel">
            <div className="panel-title">
              <span>
                <h2>일주일 안에 연락할 고객</h2>
                <p>
                  {reminderState.deliveryEnabled
                    ? "예약 알림이 자동으로 나가고 있어요."
                    : "자동 발송이 아직 꺼져 있어요. 직접 연락할 분들만 모아 보여드려요."}
                </p>
              </span>
              <button
                className="button button-outline"
                onClick={() => void loadReminders()}
              >
                새로고침
              </button>
            </div>
            <div className="reminder-list">
              {reminderState.reminders.map((reminder) => (
                <article key={reminder.id}>
                  <span>
                    <b>
                      {reminder.customer_name} · {reminder.service_name}
                    </b>
                    <small>
                      {koDate.format(new Date(reminder.scheduled_at))}{" "}
                      {koTime.format(new Date(reminder.scheduled_at))} ·{" "}
                      {reminder.kind === "booking_reminder_24h"
                        ? "24시간 전"
                        : "2시간 전"}
                    </small>
                    <small>
                      {reminder.customer_email || reminder.customer_phone}
                    </small>
                  </span>
                  <em>{reminderStatusLabel(reminder.status)}</em>
                </article>
              ))}
              {reminderState.reminders.length === 0 && (
                <p className="calendar-empty">
                  일주일 안에 연락할 고객이 없어요.
                </p>
              )}
            </div>
          </section>
        </div>
      )}
    </section>
  );
}
